import { useEffect, useMemo, useRef, useState } from "react";
import { FileRejection } from "react-dropzone";
import { RiDeleteBin6Line, RiUploadCloud2Line } from "react-icons/ri";
import BarcodeDropZone from "./BarcodeDropZone";
import BarcodeBoard, { BoardView } from "./BarcodeBoard";
import BulkEditBar from "./BulkEditBar";
import PhotoEditPopover, { ProductOption } from "./PhotoEditPopover";
import { parsePhotoDraft, deriveNames } from "./parsePhotoDraft";
import { DIRECTIONS, PhotoDraft, PhotoSection, DirectionCode, FileWithPath, productKeyOf } from "./photoModel";
import { listExistingPhotos, findConflicts, ExistingPhoto } from "./conflicts";
import { occupiedSectionIds } from "./treeModel";
import { buildPreflight, buildReport } from "./reportModel";
import ConfirmFacts, { Fact } from "./ConfirmFacts";
import UploadReport from "./UploadReport";
import { useProductValidation } from "./useProductValidation";
import { useUploadQueue, UploadTarget } from "./useUploadQueue";
import { markPackageCaseUpdated } from "../../../apis/product";
import ModalConfirm from "../../../components/ModalConfirm";

const VIEW_KEY = "importPhoto.boardView";

const angleOf = (code: DirectionCode) => DIRECTIONS.find((d) => d.code === code)?.angle ?? code;

export default function BarcodeUploadTab({ onBusyChange }: { onBusyChange?: (busy: boolean) => void }) {
  const [drafts, setDrafts] = useState<PhotoDraft[]>([]);
  const [looseFiles, setLooseFiles] = useState<FileWithPath[]>([]);
  const [rejections, setRejections] = useState<FileRejection[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [openId, setOpenId] = useState<string | null>(null);
  const [existing, setExisting] = useState<Record<string, ExistingPhoto[]>>({});
  const existingReq = useRef<Set<string>>(new Set());
  const [confirm, setConfirm] = useState<null | "clear" | "upload">(null);
  // The uploader's last choice of layout, remembered per browser.
  const [view, setView] = useState<BoardView>(() => {
    try {
      return window.localStorage.getItem(VIEW_KEY) === "grid" ? "grid" : "tree";
    } catch {
      return "tree";
    }
  });
  // Sections that have held a photo in this batch. A section emptied by a move keeps its
  // row, because it existed in the folder the user dropped.
  const [ghostSections, setGhostSections] = useState<Set<string>>(new Set());
  // Set once an upload run has finished; the receipt then stays until the batch is cleared.
  const [showReport, setShowReport] = useState(false);
  // Bumped after an upload run so the board moves onto the leftovers by itself.
  const [pendingSignal, setPendingSignal] = useState(0);

  const fileNames = useMemo(() => {
    const derived = deriveNames(drafts);
    return Object.fromEntries(Object.entries(derived).map(([id, v]) => [id, v.fileName]));
  }, [drafts]);

  const productKeys = useMemo(
    () => Array.from(new Set(drafts.map((d) => productKeyOf(d.barcode, d.matCode)))),
    [drafts]
  );
  const productInfo = useProductValidation(productKeys);
  const { states: uploads, isUploading, start, retry, reset } = useUploadQueue();
  useEffect(() => onBusyChange?.(isUploading), [isUploading, onBusyChange]);

  // Conflicts: only for products whose existing photos have been loaded.
  const derivedNames = useMemo(() => deriveNames(drafts), [drafts]);
  const conflicts = useMemo(() => {
    const all = new Set<string>();
    for (const key of Object.keys(existing)) {
      const [barcode, matCode] = key.split("/");
      const derivedForProduct = drafts
        .filter((d) => d.barcode === barcode && d.matCode === matCode && derivedNames[d.id])
        .map((d) => derivedNames[d.id].key);
      findConflicts(derivedForProduct, existing[key].map((e) => e.relPath)).forEach((k) => all.add(k));
    }
    return all;
  }, [existing, drafts, derivedNames]);

  const products: ProductOption[] = useMemo(
    () => productKeys.map((k) => { const [barcode, matCode] = k.split("/"); return { productKey: k, barcode, matCode, label: `${barcode} › ${matCode}` }; }),
    [productKeys]
  );

  // Revoke object URLs on unmount.
  const draftsRef = useRef<PhotoDraft[]>(drafts);
  draftsRef.current = drafts;
  useEffect(
    () => () => {
      draftsRef.current.forEach((d) => URL.revokeObjectURL(d.previewUrl));
    },
    []
  );

  const handleFilesAdded = (accepted: FileWithPath[], newRejections: FileRejection[]) => {
    // Compute parsed results once outside any updater to avoid double-firing under React 18 StrictMode.
    // We snapshot `drafts` here; the updater below uses it only for the id-dedup check.
    const existingIds = new Set(drafts.map((d) => d.id));
    const additions: PhotoDraft[] = [];
    const loose: FileWithPath[] = [];
    accepted.forEach((file, i) => {
      const url = URL.createObjectURL(file);
      const d = parsePhotoDraft(file, drafts.length + i, url);
      if (!d) { URL.revokeObjectURL(url); loose.push(file); return; }
      if (existingIds.has(d.id)) { URL.revokeObjectURL(url); return; }
      existingIds.add(d.id); // prevent intra-batch duplicates
      additions.push(d);
    });
    if (additions.length) setDrafts((prev) => [...prev, ...additions]);
    if (loose.length) setLooseFiles((p) => [...p, ...loose]);
    if (newRejections.length) setRejections((p) => [...p, ...newRejections]);
  };

  const patchDraft = (id: string, patch: Partial<PhotoDraft>) =>
    setDrafts((prev) => prev.map((d) => (d.id === id ? { ...d, ...patch } : d)));
  const moveDraft = (id: string, barcode: string, matCode: string) => patchDraft(id, { barcode, matCode });
  const setMain = (id: string) =>
    setDrafts((prev) => {
      const target = prev.find((d) => d.id === id);
      if (!target) return prev;
      return prev.map((d) =>
        d.matCode === target.matCode && d.barcode === target.barcode ? { ...d, isMain: d.id === id } : d
      );
    });

  const toggleSelect = (id: string) =>
    setSelected((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const bulkSection = (s: PhotoSection) => setDrafts((prev) => prev.map((d) => (selected.has(d.id) ? { ...d, section: s } : d)));
  const bulkDirection = (dir: DirectionCode) => setDrafts((prev) => prev.map((d) => (selected.has(d.id) ? { ...d, direction: dir, parseState: "confident" } : d)));
  const bulkDelete = () => {
    setDrafts((prev) => { prev.filter((d) => selected.has(d.id)).forEach((d) => URL.revokeObjectURL(d.previewUrl)); return prev.filter((d) => !selected.has(d.id)); });
    setSelected(new Set());
  };

  // Delete a single image from the batch.
  const removeDraft = (id: string) => {
    setDrafts((prev) => {
      const target = prev.find((d) => d.id === id);
      if (target) URL.revokeObjectURL(target.previewUrl);
      return prev.filter((d) => d.id !== id);
    });
    setSelected((prev) => { const n = new Set(prev); n.delete(id); return n; });
    if (openId === id) setOpenId(null);
  };

  // Delete every image of one product (a whole matcode card).
  const changeView = (v: BoardView) => {
    setView(v);
    try {
      window.localStorage.setItem(VIEW_KEY, v);
    } catch {
      /* private browsing — the layout just resets next visit */
    }
  };

  // Folder-level selection: one checkbox sets every photo beneath it.
  const setSelection = (ids: string[], on: boolean) =>
    setSelected((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => (on ? next.add(id) : next.delete(id)));
      return next;
    });

  const setDirectionFor = (id: string, direction: DirectionCode) =>
    patchDraft(id, { direction, parseState: "confident" });

  const moveSection = (ids: string[], section: PhotoSection) => {
    const targets = new Set(ids);
    setDrafts((prev) => prev.map((d) => (targets.has(d.id) ? { ...d, section } : d)));
  };

  const deleteMany = (ids: string[]) => {
    const targets = new Set(ids);
    setDrafts((prev) => {
      prev.filter((d) => targets.has(d.id)).forEach((d) => URL.revokeObjectURL(d.previewUrl));
      return prev.filter((d) => !targets.has(d.id));
    });
    setSelected((prev) => {
      const next = new Set(prev);
      targets.forEach((id) => next.delete(id));
      return next;
    });
    if (openId && targets.has(openId)) setOpenId(null);
  };

  const dismissSection = (id: string) =>
    setGhostSections((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    });

  const removeProduct = (productKey: string) => {
    setDrafts((prev) => {
      prev.filter((d) => productKeyOf(d.barcode, d.matCode) === productKey).forEach((d) => URL.revokeObjectURL(d.previewUrl));
      return prev.filter((d) => productKeyOf(d.barcode, d.matCode) !== productKey);
    });
    setSelected(new Set());
    if (openId) {
      const od = drafts.find((d) => d.id === openId);
      if (od && productKeyOf(od.barcode, od.matCode) === productKey) setOpenId(null);
    }
  };

  // Clear the whole pending batch and start over.
  const clearAll = () => {
    drafts.forEach((d) => URL.revokeObjectURL(d.previewUrl));
    setDrafts([]);
    setLooseFiles([]);
    setRejections([]);
    setSelected(new Set());
    setGhostSections(new Set());
    setShowReport(false);
    setPendingSignal(0);
    setExisting({});
    existingReq.current.clear();
    setOpenId(null);
    reset();
  };

  // Eagerly load existing photos for every VALID product (once each), so the
  // "ซ้ำ" badges and the "ซ้ำของเดิม" filter reflect real conflicts from the
  // start — not only after a popover is opened. Unknown products have nothing to
  // compare against and are skipped.
  useEffect(() => {
    for (const key of productKeys) {
      if (existingReq.current.has(key)) continue;
      if (productInfo[key]?.status !== "valid") continue;
      existingReq.current.add(key);
      const [barcode, matCode] = key.split("/");
      listExistingPhotos(barcode, matCode)
        .then((photos) => setExisting((prev) => ({ ...prev, [key]: photos })))
        .catch(() => setExisting((prev) => ({ ...prev, [key]: [] })));
    }
  }, [productKeys, productInfo]);

  const uploadable = drafts.filter(
    (d) =>
      d.direction &&
      productInfo[productKeyOf(d.barcode, d.matCode)]?.status === "valid" &&
      uploads[d.id]?.status !== "success"
  );
  const handleRetry = (id: string) => {
    if (isUploading) return;
    const d = drafts.find((x) => x.id === id);
    if (!d || !d.direction || !derivedNames[id]) return;
    const target: UploadTarget = {
      id: d.id,
      key: derivedNames[id].key,
      file: d.file,
      metadata: { barcode: d.barcode, matcode: d.matCode, section: d.section, direction: angleOf(d.direction as DirectionCode) },
    };
    retry(target);
  };

  const handleUpload = async () => {
    // The editor edits something that is about to be sent, and it now lives inside the
    // action bar that the receipt replaces — so a run always starts with it closed.
    setOpenId(null);
    const targets: UploadTarget[] = uploadable
      .filter((d) => derivedNames[d.id])
      .map((d) => ({
        id: d.id,
        key: derivedNames[d.id].key,
        file: d.file,
        metadata: { barcode: d.barcode, matcode: d.matCode, section: d.section, direction: angleOf(d.direction as DirectionCode) },
      }));
    const succeeded = await start(targets);
    const matcodes = new Set(succeeded.map((id) => drafts.find((d) => d.id === id)?.matCode).filter(Boolean) as string[]);
    await Promise.all(Array.from(matcodes).map((mc) => markPackageCaseUpdated(mc)));
    setShowReport(true);
    setPendingSignal((n) => n + 1);
  };

  /** Re-send every photo that failed, through the same queue the first run used. */
  const retryFailed = async (ids: string[]) => {
    if (isUploading) return;
    const wanted = new Set(ids);
    const targets: UploadTarget[] = drafts
      .filter((d) => wanted.has(d.id) && d.direction && derivedNames[d.id])
      .map((d) => ({
        id: d.id,
        key: derivedNames[d.id].key,
        file: d.file,
        metadata: { barcode: d.barcode, matcode: d.matCode, section: d.section, direction: angleOf(d.direction as DirectionCode) },
      }));
    if (targets.length === 0) return;
    const succeeded = await start(targets);
    const matcodes = new Set(succeeded.map((id) => drafts.find((d) => d.id === id)?.matCode).filter(Boolean) as string[]);
    await Promise.all(Array.from(matcodes).map((mc) => markPackageCaseUpdated(mc)));
  };

  useEffect(() => {
    const occupied = occupiedSectionIds(drafts);
    setGhostSections((prev) => {
      let changed = false;
      const next = new Set(prev);
      occupied.forEach((id) => {
        if (!next.has(id)) {
          next.add(id);
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [drafts]);

  const report = useMemo(
    () => buildReport({ drafts, uploads, derivedNames, productInfo }),
    [drafts, uploads, derivedNames, productInfo]
  );
  const preflight = useMemo(
    () => buildPreflight({ drafts, uploads, derivedNames, productInfo, conflicts }),
    [drafts, uploads, derivedNames, productInfo, conflicts]
  );

  // Everything the confirm dialogs need, so neither one depends on the page behind it.
  const uploadFacts = [
    preflight.overwrites > 0 &&
      { tone: "dup", text: `${preflight.overwrites} รูปจะเขียนทับรูปเดิมที่มีอยู่แล้ว` },
    preflight.noDirection > 0 &&
      { tone: "warn", text: `${preflight.noDirection} รูปยังไม่ได้เลือกมุม จะไม่ถูกส่ง` },
    preflight.noProduct > 0 &&
      { tone: "error", text: `${preflight.noProduct} รูปไม่พบ matcode ในระบบ จะไม่ถูกส่ง` },
    preflight.alreadyUploaded > 0 &&
      { tone: "ready", text: `${preflight.alreadyUploaded} รูปอัปโหลดไปแล้ว ไม่ส่งซ้ำ` },
  ].filter(Boolean) as Fact[];

  const clearFacts = [
    preflight.alreadyUploaded > 0 &&
      { tone: "ready", text: `${preflight.alreadyUploaded} รูปที่อัปโหลดแล้วยังอยู่ในระบบ ไม่ถูกลบ` },
    { tone: "warn", text: "การกระทำนี้ย้อนกลับไม่ได้" },
  ].filter(Boolean) as Fact[];

  // Everything landed: the working list has nothing left to say, so the receipt replaces it.
  const reportVisible = showReport && drafts.length > 0;
  const listHidden = reportVisible && report.complete;

  const openDraft = drafts.find((d) => d.id === openId) ?? null;

  return (
    <div className="max-w-6xl mx-auto px-4 text-left">
      {!listHidden && (
        <BarcodeDropZone onFilesAdded={handleFilesAdded} disabled={isUploading} compact={drafts.length > 0} />
      )}

      {reportVisible && (
        <UploadReport
          report={report}
          busy={isUploading}
          onRetry={handleRetry}
          onRetryAll={retryFailed}
          onStartOver={clearAll}
        />
      )}

      {/* Loose files + rejections list — Thai copy preserved from the previous tab */}
      {(looseFiles.length > 0 || rejections.length > 0) && (
        <div className="p-4 mt-4 border rounded-xl border-amber-200 bg-amber-50">
          <p className="flex items-center gap-1.5 mb-2 text-sm font-semibold text-amber-800">
            <span aria-hidden="true">⚠</span>
            ข้าม {looseFiles.length + rejections.length} ไฟล์
          </p>
          <ul className="space-y-1 text-xs text-amber-700 leading-relaxed">
            {looseFiles.map((file, index) => (
              <li key={`loose_${file.name}_${index}`} className="truncate">
                <span className="font-medium">{file.path ?? file.name}</span>{" "}
                — โครงสร้างไม่ถูกต้อง (ต้องเป็น barcode/matcode/cover|packshot/ไฟล์)
              </li>
            ))}
            {rejections.map((rejection, index) => (
              <li key={`reject_${rejection.file.name}_${index}`} className="truncate">
                <span className="font-medium">{rejection.file.name}</span> —{" "}
                {rejection.errors.map((e) => {
                  switch (e.code) {
                    case "file-too-large": return "ไฟล์มีขนาดใหญ่เกิน 20 MB";
                    case "file-invalid-type": return "ชนิดไฟล์ไม่รองรับ (รับเฉพาะ .jpg .jpeg .png)";
                    case "name-not-in-english": return "ชื่อไฟล์ต้องเป็นภาษาอังกฤษเท่านั้น";
                    default: return e.message;
                  }
                }).join(", ")}
              </li>
            ))}
          </ul>
        </div>
      )}

      {drafts.length > 0 && !listHidden && (
        <>
          <div className="mt-4">
            <BarcodeBoard drafts={drafts} productInfo={productInfo} fileNames={fileNames} conflicts={conflicts}
              selected={selected} uploads={uploads} ghostSections={ghostSections} disabled={isUploading}
              pendingSignal={pendingSignal}
              view={view} onViewChange={changeView}
              onToggleSelect={toggleSelect} onSetSelection={setSelection} onOpen={setOpenId}
              onSetDirection={setDirectionFor} onMoveSection={moveSection} onSetMain={setMain}
              onDeleteMany={isUploading ? () => undefined : deleteMany}
              onDismissSection={isUploading ? undefined : dismissSection}
              onRetry={isUploading ? undefined : handleRetry}
              onDelete={isUploading ? undefined : removeDraft}
              onDeleteGroup={isUploading ? undefined : removeProduct} />
          </div>
          {/* Spacer so the last cards can scroll clear of the fixed footer. */}
          <div aria-hidden="true" className="h-20" />

          {/* Fixed action footer — always pinned to the viewport bottom regardless
              of how many cards are added. Bulk bar (when active) stacks above the
              clear/upload row. */}
          <div className="fixed inset-x-0 bottom-0 z-40">
            {/* The editor rides on the action bar, never over it. Anchored at `bottom-full`
                it clears whatever height the bar currently has — one row, or two once the
                bulk bar appears — so the primary "อัปโหลด" button stays reachable while a
                photo is open. */}
            {openDraft && (
              <div className="absolute bottom-full right-4 mb-3">
                <PhotoEditPopover key={openDraft.id} draft={openDraft} fileName={fileNames[openDraft.id]} products={products}
                  existing={existing[productKeyOf(openDraft.barcode, openDraft.matCode)]}
                  onChange={(id, patch) => patchDraft(id, { ...patch, parseState: patch.direction ? "confident" : openDraft.parseState })}
                  onSetMain={setMain} onMove={moveDraft} onClose={() => setOpenId(null)} />
              </div>
            )}
            <div className="border-t border-gray-200 bg-white/95 backdrop-blur">
              <BulkEditBar count={selected.size} onSetSection={bulkSection} onSetDirection={bulkDirection} onDelete={bulkDelete} onClear={() => setSelected(new Set())} />
              <div className="flex items-center justify-end gap-2 max-w-6xl mx-auto px-4 py-1.5">
                <button
                  type="button"
                  onClick={() => setConfirm("clear")}
                  disabled={isUploading}
                  title="ล้างรายการทั้งหมดที่รออัปโหลด"
                  className="mr-auto inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium text-gray-500 transition-colors hover:text-red-600 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <RiDeleteBin6Line size={16} aria-hidden="true" />
                  ล้างทั้งหมด
                  <span className="text-xs font-normal text-gray-400">({drafts.length} รูป)</span>
                </button>
                {uploadable.length === 0 && !isUploading && (
                  <span className="text-xs text-gray-400">ยังไม่มีรูปที่พร้อมอัปโหลด</span>
                )}
                <button
                  type="button"
                  onClick={() => setConfirm("upload")}
                  disabled={isUploading || uploadable.length === 0}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-gradient-to-r from-blue-500 to-indigo-600 px-5 py-2 text-sm font-semibold text-white shadow-sm transition-opacity hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {isUploading ? (
                    <>
                      <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" aria-hidden="true" />
                      กำลังอัปโหลด…
                    </>
                  ) : (
                    <>
                      <RiUploadCloud2Line size={16} aria-hidden="true" />
                      อัปโหลด
                      <span className="text-xs font-normal text-white/70">({uploadable.length} รูป)</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
      <ModalConfirm
        show={confirm === "clear"}
        title="ล้างรายการทั้งหมด?"
        body={`ล้างรูปที่รออัปโหลด ${preflight.totalPhotos} รูป ใน ${preflight.totalMatcodes} matcode ออกจากหน้านี้`}
        children={<ConfirmFacts facts={clearFacts} />}
        okText="ยืนยันล้าง"
        cancelText="ยกเลิก"
        okClass="justify-center px-4 py-2 text-md font-medium text-white bg-red-600 rounded hover:bg-red-700"
        okAction={() => { clearAll(); setConfirm(null); }}
        onHide={() => setConfirm(null)}
      />
      <ModalConfirm
        show={confirm === "upload"}
        title="ยืนยันการอัปโหลด?"
        body={`อัปโหลด ${preflight.sendPhotos} รูป ใน ${preflight.sendMatcodes} matcode เข้าสู่ระบบ`}
        children={<ConfirmFacts facts={uploadFacts} />}
        okText="ยืนยันอัปโหลด"
        cancelText="ยกเลิก"
        okAction={() => { setConfirm(null); handleUpload(); }}
        onHide={() => setConfirm(null)}
      />
    </div>
  );
}
